// ============================================================================
// KLINISOL CRO - Form Automation API Service
// File: src/services/formApi.js
// Purpose: All API calls for form automation functionality
// FDA 21 CFR Part 11 Compliant
// ============================================================================

import api from './api'; // Your existing axios instance

const formApi = {
    // ========================================================================
    // FORM SCHEMAS
    // ========================================================================
    
    /**
     * Get all active form schemas
     * @param {Object} params - {status, category}
     * @returns {Promise<Array>} List of schemas
     */
    getAllSchemas: async (params = {}) => {
        try {
            const response = await api.get('/forms/schemas', { params });
            return response.data.schemas;
        } catch (error) {
            console.error('Error fetching form schemas:', error);
            throw error;
        }
    },
    
    /**
     * Get specific form schema by ID with full details
     * @param {number} schemaId - Form schema ID
     * @returns {Promise<Object>} Schema with JSON structure
     */
    getSchemaById: async (schemaId) => {
        try {
            const response = await api.get(`/forms/schemas/${schemaId}`);
            return response.data.schema;
        } catch (error) {
            console.error('Error fetching schema:', error);
            throw error;
        }
    },
    
    /**
     * Create new form schema (Admin only)
     * @param {Object} data - Schema data
     * @returns {Promise<Object>} Created schema
     */
    createSchema: async (data) => {
        try {
            const response = await api.post('/forms/schemas', data);
            return response.data;
        } catch (error) {
            console.error('Error creating schema:', error);
            throw error;
        }
    },
    
    /**
     * Update form schema (Admin only)
     * @param {number} schemaId - Schema ID
     * @param {Object} data - Update data
     * @returns {Promise<Object>} Updated schema
     */
    updateSchema: async (schemaId, data) => {
        try {
            const response = await api.put(`/forms/schemas/${schemaId}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating schema:', error);
            throw error;
        }
    },
    
    /**
     * Retire form schema
     * @param {number} schemaId - Schema ID
     * @returns {Promise<Object>} Success message
     */
    retireSchema: async (schemaId) => {
        try {
            const response = await api.patch(`/forms/schemas/${schemaId}/retire`);
            return response.data;
        } catch (error) {
            console.error('Error retiring schema:', error);
            throw error;
        }
    },
    
    /**
     * Get available form templates
     * @returns {Promise<Array>} List of templates
     */
    getFormTemplates: async () => {
        try {
            const response = await api.get('/forms/templates');
            return response.data.templates;
        } catch (error) {
            console.error('Error fetching templates:', error);
            throw error;
        }
    },
    
    // ========================================================================
    // FORM SUBMISSIONS
    // ========================================================================
    
    /**
     * Create new form submission (draft mode)
     * @param {Object} data - {formSchemaId, submissionData}
     * @returns {Promise<Object>} Created submission with auto-populated fields
     */
    createSubmission: async (data) => {
        try {
            const response = await api.post('/forms/submissions', data);
            return response.data;
        } catch (error) {
            console.error('Error creating submission:', error);
            throw error;
        }
    },
    
    /**
     * Get user's form submissions
     * @param {Object} params - {status, limit, offset}
     * @returns {Promise<Array>} List of submissions
     */
    getUserSubmissions: async (params = {}) => {
        try {
            const response = await api.get('/forms/submissions', { params });
            return response.data.submissions;
        } catch (error) {
            console.error('Error fetching submissions:', error);
            throw error;
        }
    },
    
    /**
     * Get submission details by ID
     * @param {number} submissionId - Submission ID
     * @returns {Promise<Object>} Full submission data with workflow steps
     */
    getSubmissionById: async (submissionId) => {
        try {
            const response = await api.get(`/forms/submissions/${submissionId}`);
            return response.data.submission;
        } catch (error) {
            console.error('Error fetching submission:', error);
            throw error;
        }
    },
    
    /**
     * Update form submission (draft mode only)
     * @param {number} submissionId - Submission ID
     * @param {Object} data - {submissionData}
     * @returns {Promise<Object>} Update result with changed fields
     */
    updateSubmission: async (submissionId, data) => {
        try {
            const response = await api.put(`/forms/submissions/${submissionId}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating submission:', error);
            throw error;
        }
    },
    
    /**
     * Submit form for workflow processing
     * @param {number} submissionId - Submission ID
     * @returns {Promise<Object>} Workflow steps created
     */
    submitForApproval: async (submissionId) => {
        try {
            const response = await api.post(`/forms/submissions/${submissionId}/submit`);
            return response.data;
        } catch (error) {
            console.error('Error submitting form:', error);
            throw error;
        }
    },
    
    /**
     * Delete submission (draft only)
     * @param {number} submissionId - Submission ID
     * @returns {Promise<Object>} Success message
     */
    deleteSubmission: async (submissionId) => {
        try {
            const response = await api.delete(`/forms/submissions/${submissionId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting submission:', error);
            throw error;
        }
    },
    
    // ========================================================================
    // WORKFLOW MANAGEMENT
    // ========================================================================
    
    /**
     * Get pending approvals for current user
     * @returns {Promise<Array>} Pending workflow steps
     */
    getPendingApprovals: async () => {
        try {
            const response = await api.get('/forms/workflow/pending');
            return response.data.pending_approvals;
        } catch (error) {
            console.error('Error fetching pending approvals:', error);
            throw error;
        }
    },
    
    /**
     * Complete workflow step with electronic signature
     * @param {number} stepId - Workflow step ID
     * @param {Object} data - {signatureText, pin, signatureMeaning, comments}
     * @returns {Promise<Object>} Signature result
     */
    completeWorkflowStep: async (stepId, data) => {
        try {
            const response = await api.post(`/forms/workflow/steps/${stepId}/complete`, data);
            return response.data;
        } catch (error) {
            console.error('Error completing workflow step:', error);
            throw error;
        }
    },
    
    /**
     * Reject workflow step with reason
     * @param {number} stepId - Workflow step ID
     * @param {Object} data - {rejectionReason, pin}
     * @returns {Promise<Object>} Rejection result
     */
    rejectWorkflowStep: async (stepId, data) => {
        try {
            const response = await api.post(`/forms/workflow/steps/${stepId}/reject`, data);
            return response.data;
        } catch (error) {
            console.error('Error rejecting workflow step:', error);
            throw error;
        }
    },
    
    /**
     * Get workflow history for submission
     * @param {number} submissionId - Submission ID
     * @returns {Promise<Array>} Workflow steps with signatures
     */
    getWorkflowHistory: async (submissionId) => {
        try {
            const response = await api.get(`/forms/workflow/submissions/${submissionId}/history`);
            return response.data.workflow_history;
        } catch (error) {
            console.error('Error fetching workflow history:', error);
            throw error;
        }
    },
    
    /**
     * Verify workflow signature
     * @param {number} signatureId - Signature ID
     * @returns {Promise<Object>} Signature verification data
     */
    verifySignature: async (signatureId) => {
        try {
            const response = await api.get(`/forms/workflow/signatures/${signatureId}/verify`);
            return response.data.signature;
        } catch (error) {
            console.error('Error verifying signature:', error);
            throw error;
        }
    },
    
    // ========================================================================
    // AUDIT & COMPLIANCE
    // ========================================================================
    
    /**
     * Get complete audit trail for submission
     * @param {number} submissionId - Submission ID
     * @returns {Promise<Array>} Chronological audit trail
     */
    getSubmissionAuditTrail: async (submissionId) => {
        try {
            const response = await api.get(`/forms/audit/submissions/${submissionId}`);
            return response.data.audit_trail;
        } catch (error) {
            console.error('Error fetching audit trail:', error);
            throw error;
        }
    },
    
    /**
     * Get field change history
     * @param {number} submissionId - Submission ID
     * @param {string} fieldName - Optional field name filter
     * @returns {Promise<Array>} Field modifications
     */
    getFieldChangeHistory: async (submissionId, fieldName = null) => {
        try {
            const params = fieldName ? { fieldName } : {};
            const response = await api.get(`/forms/audit/submissions/${submissionId}/fields`, { params });
            return response.data.changes;
        } catch (error) {
            console.error('Error fetching field history:', error);
            throw error;
        }
    },
    
    /**
     * Get user activity report
     * @param {number} userId - User ID
     * @param {Object} params - {startDate, endDate}
     * @returns {Promise<Object>} Activity report with statistics
     */
    getUserActivityReport: async (userId, params = {}) => {
        try {
            const response = await api.get(`/forms/audit/users/${userId}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching user activity:', error);
            throw error;
        }
    },
    
    /**
     * Get signature audit report (Admin only)
     * @param {Object} params - {startDate, endDate, userId}
     * @returns {Promise<Object>} Signature report with statistics
     */
    getSignatureAuditReport: async (params = {}) => {
        try {
            const response = await api.get('/forms/audit/signatures', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching signature audit:', error);
            throw error;
        }
    },
    
    /**
     * Get workflow performance metrics (Admin only)
     * @param {Object} params - {startDate, endDate}
     * @returns {Promise<Object>} Performance metrics
     */
    getWorkflowMetrics: async (params = {}) => {
        try {
            const response = await api.get('/forms/audit/workflow/metrics', { params });
            return response.data.metrics;
        } catch (error) {
            console.error('Error fetching workflow metrics:', error);
            throw error;
        }
    },
    
    /**
     * Get FDA 21 CFR Part 11 compliance report (Admin only)
     * @param {Object} params - {startDate, endDate}
     * @returns {Promise<Object>} Compliance status report
     */
    getComplianceReport: async (params = {}) => {
        try {
            const response = await api.get('/forms/audit/compliance-report', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching compliance report:', error);
            throw error;
        }
    }
};

export default formApi;
